import { apiRequest } from 'utils/api';

export const notificationType = {
  guestAnswerCreatedOrUpdated: 'guest-answer-created-or-updated',
  subscriptionExpiresSoon: 'subscription-expires-soon',
  invitationsSent: 'invitations-sent',
  adminUserRegistered: 'admin-user-registered',
  adminEventCreated: 'admin-event-created'
};

function toRelativeTimeLabel(occurredAtUtc) {
  const occurredDate = new Date(occurredAtUtc);
  if (Number.isNaN(occurredDate.getTime())) {
    return 'Just now';
  }

  const diffMinutes = Math.round((occurredDate.getTime() - Date.now()) / 60000);
  const absDiffMinutes = Math.abs(diffMinutes);

  if (absDiffMinutes < 1) {
    return 'Just now';
  }

  if (absDiffMinutes < 60) {
    return `${absDiffMinutes} min ago`;
  }

  if (absDiffMinutes < 60 * 24) {
    return `${Math.round(absDiffMinutes / 60)} hours ago`;
  }

  return occurredDate.toLocaleDateString();
}

function toClockLabel(occurredAtUtc) {
  const occurredDate = new Date(occurredAtUtc);
  if (Number.isNaN(occurredDate.getTime())) {
    return '-';
  }

  return occurredDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function mapApiNotification(item) {
  const fallbackId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  return {
    id: String(item?.id || fallbackId),
    type: String(item?.type || ''),
    title: String(item?.title || 'Notification'),
    message: String(item?.message || ''),
    secondary: toRelativeTimeLabel(item?.occurredAtUtc),
    timeLabel: toClockLabel(item?.occurredAtUtc),
    iconKey: String(item?.iconKey || 'message'),
    color: String(item?.color || 'primary.main'),
    backgroundColor: String(item?.backgroundColor || 'primary.lighter'),
    isUnread: Boolean(item?.isUnread),
    actionUrl: String(item?.actionUrl || '/events')
  };
}

export async function getNotificationsFromApi() {
  const response = await apiRequest('/api/notifications');

  if (!response.ok) {
    throw new Error(`Notifications endpoint returned ${response.status}`);
  }

  const payload = await response.json();
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.map(mapApiNotification);
}

export async function markNotificationReadInApi(notificationId) {
  if (!notificationId) {
    return;
  }

  const response = await apiRequest(`/api/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'POST'
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Mark notification read failed with ${response.status}`);
  }
}

export async function markAllNotificationsReadInApi() {
  const response = await apiRequest('/api/notifications/read-all', {
    method: 'POST'
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Mark all notifications read failed with ${response.status}`);
  }
}

export async function deleteAllNotificationsInApi() {
  const response = await apiRequest('/api/notifications/delete-all', {
    method: 'POST'
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Delete all notifications failed with ${response.status}`);
  }
}

export async function deleteNotificationsOlderThanMonthsInApi(months) {
  const response = await apiRequest(`/api/notifications/delete-older/${months}`, {
    method: 'POST'
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Delete notifications older than ${months} months failed with ${response.status}`);
  }
}

export function getSeedNotifications() {
  return [
    {
      id: 'guest-answer-updated-1',
      type: notificationType.guestAnswerCreatedOrUpdated,
      title: 'Guest answer updated',
      message: 'Maria Papadopoulou changed her RSVP for Summer Wedding 2026.',
      secondary: '2 min ago',
      timeLabel: '3:00 AM',
      iconKey: 'message',
      color: 'primary.main',
      backgroundColor: 'primary.lighter',
      isUnread: true,
      actionUrl: '/events'
    },
    {
      id: 'subscription-expire-5-days',
      type: notificationType.subscriptionExpiresSoon,
      title: 'Subscription expires in 5 days',
      message: 'Your plan is close to expiring. A reminder is sent daily while 5 days remain.',
      secondary: 'Today',
      timeLabel: '6:00 AM',
      iconKey: 'warning',
      color: 'warning.main',
      backgroundColor: 'warning.lighter',
      isUnread: true,
      actionUrl: '/billing/history'
    },
    {
      id: 'invitations-sent-1',
      type: notificationType.invitationsSent,
      title: 'Invitations were sent',
      message: 'Event invitations for Team Meetup were delivered successfully.',
      secondary: '7 hours ago',
      timeLabel: '2:45 PM',
      iconKey: 'success',
      color: 'success.main',
      backgroundColor: 'success.lighter',
      isUnread: false,
      actionUrl: '/events'
    }
  ];
}
